import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    segmentTrackBulkArchiveScenarios,
    segmentTrackBulkRerunScenarios,
    segmentTrackSubmitScenarioRun
} from "in_queue/analytics";
import {
    getScenarioDialogConfigFromExistingScenario,
    useCreateScenarios
} from "in_queue/components/ScenarioBuilder/scenarioBuilder.helpers";
import { getWholeProjectOnlyProjectIds } from "in_queue/components/ScenarioDialog/wholeProjectOnly";
import { ArchiveOutcome } from "in_queue/components/ScenarioRerun/bulkRerunToast";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    useAllAvailableProjects,
    useSpeculativeProjectsInCluster,
    useTrackedProjectsInCluster
} from "in_queue/contexts/ProjectDataContext";
import { ProjectType, TrackedProject } from "in_queue/types/projectType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import {
    getParentProject,
    getParentProjectId
} from "in_queue/utils/scenarioParentProjects";
import { useCallback, useMemo, useState } from "react";

export interface CreatedScenario {
    scenarioId: string;
    title: string;
}

export interface ScenarioRerunRequest {
    scenario: ScenarioMetadata;
    title: string;
}

export interface BulkRerunResult {
    created: ReadonlyArray<CreatedScenario>;
    archive: ArchiveOutcome;
    // Projects whose saved reduction the whole-project-only rule forbids. The
    // copies run without it, so the caller has to say so.
    droppedWholeProjectOnlyIds: ReadonlyArray<string>;
}

/**
 * Duplicate each given scenario and immediately queue the copy for a run.
 *
 * With `archiveOriginals`, the copies and the archive land in one transaction,
 * so a failed duplication can never retire the scenario it was copying.
 */
export const useBulkRerunScenarios = ({
    project
}: {
    project: TrackedProject;
}): {
    rerunScenarios: (
        requests: ReadonlyArray<ScenarioRerunRequest>,
        archiveOriginals: boolean
    ) => Promise<BulkRerunResult>;
    isRerunning: boolean;
} => {
    const createScenarios = useCreateScenarios();
    const cluster = useCluster();
    const userData = useUserDataContext();
    const availableProjects = useAllAvailableProjects();
    const speculativeProjects = useSpeculativeProjectsInCluster();
    const trackedProjectsInCluster = useTrackedProjectsInCluster();
    const [isRerunning, setIsRerunning] = useState(false);

    const validProjectIds = useMemo(
        () =>
            availableProjects === "loading"
                ? []
                : availableProjects.map((p) => p.projectId),
        [availableProjects]
    );
    const wholeProjectOnlyProjectIds = useMemo(
        () => getWholeProjectOnlyProjectIds(availableProjects),
        [availableProjects]
    );

    const rerunScenarios = useCallback(
        async (
            requests: ReadonlyArray<ScenarioRerunRequest>,
            archiveOriginals: boolean
        ): Promise<BulkRerunResult> => {
            if (
                availableProjects === "loading" ||
                speculativeProjects === "loading" ||
                trackedProjectsInCluster === "loading"
            ) {
                return {
                    created: [],
                    archive: { status: "skipped" },
                    droppedWholeProjectOnlyIds: []
                };
            }

            setIsRerunning(true);

            const droppedWholeProjectOnly = new Set<string>();
            const scenarioProps = requests.map(({ scenario, title }) => {
                const config = getScenarioDialogConfigFromExistingScenario({
                    projectId: project.projectId,
                    scenario,
                    action: "create",
                    scenarioStage: scenario.stage,
                    validProjectIds,
                    wholeProjectOnlyProjectIds
                });
                config.droppedWholeProjectOnlyIds.forEach((projectId) =>
                    droppedWholeProjectOnly.add(projectId)
                );
                const parentProject = getParentProject({
                    trackedProjectsInCluster,
                    parentProjectId: config.parentProjectId
                });

                return {
                    cluster,
                    project,
                    userData,
                    title,
                    runOnSubmit: true,
                    projectSizeAssumptions: config.projectSizeAssumptions,
                    scenarioStage: config.scenarioStage,
                    parentProjectId: getParentProjectId({
                        parentProject,
                        projectType: ProjectType.QUEUE_PROJECT
                    }),
                    parentSpeculativeProjectId: getParentProjectId({
                        parentProject,
                        projectType: ProjectType.SPECULATIVE_PROJECT
                    }),
                    costAssumptions: config.costAssumptions,
                    includedSpeculativeProjects: speculativeProjects.filter(
                        (sp) =>
                            config.includedSpeculativeProjectIds.includes(
                                sp.projectId
                            )
                    ),
                    customUpgradeId: config.customUpgradeId
                };
            });

            const result = await createScenarios(
                scenarioProps,
                archiveOriginals
                    ? requests.map(({ scenario }) => scenario.scenarioId)
                    : []
            ).catch(() => ({ created: [], archivedCount: 0 }));

            const created: CreatedScenario[] = result.created.map((s) => ({
                scenarioId: s.scenario_id,
                title: s.title
            }));

            created.forEach(({ scenarioId }) =>
                segmentTrackSubmitScenarioRun({
                    scenarioId,
                    source: "bulk_rerun"
                })
            );

            if (created.length > 0) {
                segmentTrackBulkRerunScenarios({
                    projectId: project.projectId,
                    numScenarios: created.length
                });
            }

            if (result.archivedCount > 0) {
                segmentTrackBulkArchiveScenarios({
                    projectId: project.projectId,
                    numScenarios: result.archivedCount,
                    numPublishedScenarios: 0
                });
            }

            const archive: ArchiveOutcome =
                archiveOriginals && created.length > 0
                    ? {
                          status: "archived",
                          archivedCount: result.archivedCount,
                          requestedCount: requests.length
                      }
                    : { status: "skipped" };

            setIsRerunning(false);
            return {
                created,
                archive,
                droppedWholeProjectOnlyIds: Array.from(droppedWholeProjectOnly)
            };
        },
        [
            availableProjects,
            cluster,
            createScenarios,
            project,
            speculativeProjects,
            trackedProjectsInCluster,
            userData,
            validProjectIds,
            wholeProjectOnlyProjectIds
        ]
    );

    return { rerunScenarios, isRerunning };
};
